import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { scroller } from 'react-scroll'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useStoryReader } from '@hooks/book/useStoryReader'
import { useAuthentication } from '@hooks/useAuthentication'
import { BookCharacterType } from '@models/book/BookCharacterType'
import { SupportCharacterFormType } from '@components/SupportCharacterModal/interface'
import { useAlert } from '@hooks/useAlert'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { useCharacterAction } from '@hooks/character/useCharacterAction'
import { WithCharacterProps, CharacterProps } from './interface'

const withCharacter = (Component: React.FC<CharacterProps>) => {
  const WithCharacter = (props: WithCharacterProps) => {
    const { user, userCoin, isAuthenticated } = useAuthentication()
    const { data } = useStoryReader()
    const characterClient = useCharacterAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const supportCharacterModal = useModal({ modal: 'supportCharacter' }, true)
    const loginModal = useModal({ modal: 'login' })
    const notEnoughCoinModal = useModal({
      modal: 'notEnoughCoin',
      modalProps: {
        describeText: '',
        purposeText: 'สนับสนุนตัวละคร',
      },
    })
    const changeEmailModal = useModal({ modal: 'changeEmail' }, true)
    const router = useRouter()

    useEffect(() => {
      const { scrollTo } = router.query
      if (!scrollTo) {
        return
      }
      scroller.scrollTo(scrollTo as string, {
        smooth: true,
        offset: -160,
        delay: 3000,
      })
      router.replace(`/story/${router.query.id}`, undefined, {
        shallow: true,
      })
    }, [router])

    function supportCharacter(character: BookCharacterType) {
      if (
        (userCoin.freeCoin && userCoin.freeCoin.value >= 1) ||
        (userCoin.paidCoin && userCoin.paidCoin.value >= 1) ||
        (userCoin.thirdCoin && userCoin.thirdCoin.value >= 1)
      ) {
        supportCharacterModal.show({
          title: data?.title,
          name: character.name,
          roleName: character.characterRole?.name,
          imgPath: character.imgPath,
          onSubmit: async ({
            freeCoin,
            paidCoin,
            thirdCoin,
          }: SupportCharacterFormType) => {
            try {
              const newData = await characterClient.createCharacterDonation(
                character.id,
                userCoin,
                freeCoin,
                paidCoin,
                thirdCoin
              )
              if (newData) {
                characterClient.triggerDonationCharacter(
                  {
                    freeCoin,
                    paidCoin,
                    thirdCoin,
                  },
                  newData.transactionId,
                  {
                    name: character.name,
                    id: character.id,
                  }
                )
                queryClient.setQueryData<StoryDetailReaderType | undefined>(
                  ['story-detail', data?.id, 'reader'],
                  (oldData?: StoryDetailReaderType) => {
                    if (oldData) {
                      return {
                        ...oldData,
                        characters: oldData.characters.map(row => {
                          if (row.id === newData.characterDonation.id) {
                            return {
                              ...row,
                              coinsTotal: newData.characterDonation.coinsTotal,
                              coinsToNextLevel:
                                newData.characterDonation.coinsToNextLevel,
                              characterRank:
                                newData.characterDonation.characterRank,
                              characterNextRank:
                                newData.characterDonation.characterNextRank,
                            }
                          }
                          return row
                        }),
                      }
                    }

                    return oldData
                  }
                )
                queryClient.invalidateQueries('userCoin')
                alert.success(
                  `คุณได้สนับสนุนตัวละคร ${character.name} เรียบร้อยแล้ว`
                )
              }
            } catch (error) {
              alert.error(
                'เกิดข้อผิดพลาด สนับสนุนตัวละครไม่สำเร็จ กรุณาลองอีกครั้ง'
              )
            } finally {
              supportCharacterModal.hide()
            }
          },
          onCancel: () => {
            supportCharacterModal.hide()
          },
        })
      } else {
        notEnoughCoinModal.show()
      }
    }

    const handleSupportCharacter = (character: BookCharacterType) => {
      if (isAuthenticated) {
        if (user.emailEditable) {
          changeEmailModal.show({
            onSuccess: () => {
              supportCharacter(character)
            },
          })
        } else {
          supportCharacter(character)
        }
      } else {
        loginModal.show()
      }
    }

    if (!data?.characters?.length) return null

    const componentProps = {
      ...props,
      bookId: data?.id,
      bookName: data?.title || '',
      characters: data?.characters || [],
      handleSupportCharacter,
    }

    return <Component {...componentProps} />
  }

  return WithCharacter
}

export { withCharacter }
